<template>
  <div>
    <div class="space-y-16">
      <div class="text-static-default-hi heading-3">
        {{ i18n(translations.title) }}
      </div>
      <RevInfoBlock
        v-if="state === 'ON_HOLD' && onHoldInfoBlockTitle && infoBlockContent"
        :content="infoBlockContent"
        :icon="IconWarning"
        :title="onHoldInfoBlockTitle"
        variant="danger"
      />
      <RevInfoBlock
        v-if="failedPaymentDuringAttemptPeriod && infoBlockContent"
        :content="infoBlockContent"
        :icon="IconWarning"
        :title="i18n(translations.failedPaymentDuringAttemptPeriodInfoTitle)"
        variant="danger"
      />
      <div v-if="dueAmount">
        <div class="text-static-default-hi body-2">
          {{ i18n(translations.totalDue) }}
        </div>
        <div class="text-static-default-hi punchline">
          {{ i18n.price(dueAmount) }}
        </div>
      </div>
      <div v-if="nextPayment">
        <div class="text-static-default-hi body-2">
          {{ i18n(translations.nextPayment) }}
        </div>
        <div class="flex items-baseline gap-4">
          <div class="text-static-default-hi heading-2">
            {{ i18n.price(nextPayment.price) }}
          </div>
          <div class="text-static-default-low body-2">
            {{
              i18n(translations.nextPaymentDate, {
                date: i18n.date(
                  new Date(nextPayment.date),
                  INSURANCE_DATE_FORMAT,
                ),
              })
            }}
          </div>
        </div>
      </div>

      <div class="space-y-16">
        <RevButton
          v-if="updatePaymentMethodButton"
          class="mt-16 md:mt-24"
          full-width="always"
          :to="updatePaymentMethodRoute"
          :tracking="{
            zone: TRACKING_ZONES.POLICY_DETAILS,
            name: 'update-payment-method',
            hasDueAmount: dueAmount !== null,
          }"
          :variant="updatePaymentMethodButton.variant"
        >
          {{ updatePaymentMethodButton.label }}
        </RevButton>

        <RevButton
          v-if="displayCancelAutoRenewalButton"
          :disabled="dueAmount !== null"
          full-width="always"
          :tracking="{
            zone: TRACKING_ZONES.POLICY_DETAILS,
            name: 'cancel-auto-renewal',
          }"
          variant="secondary"
          @click="openModal(MODAL_NAMES.CANCEL_AUTO_RENEWAL_MODAL)"
        >
          {{ i18n(translations.cancelAutoRenewalButton) }}
        </RevButton>
      </div>

      <CancelAutoRenewalModal
        v-if="displayCancelAutoRenewalButton"
        :renewal-anniversary-date
        @cancel-auto-renewal="emit('cancel-auto-renewal')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models'
import type { PaymentFrequency } from '@backmarket/http-api/src/api-specs-insurance/types'
import { PaymentFlow } from '@backmarket/nuxt-layer-payment/pages/config/flows'
import { usePaymentRouteLocation } from '@backmarket/nuxt-layer-payment/pages/navigation/usePaymentRouteLocation'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { openModal } from '@ds/components/ModalBase'
import { IconWarning } from '@ds/icons/IconWarning'

import {
  INSURANCE_DATE_FORMAT,
  MODAL_NAMES,
  TRACKING_ZONES,
} from '~/scopes/dashboard/services-hub/constant'
import type { PolicyDetails } from '~/scopes/dashboard/services-hub/types'

import translations from './PaymentDetails.translations'
import CancelAutoRenewalModal from './components/CancelAutoRenewalModal/CancelAutoRenewalModal.vue'

const i18n = useI18n()

const props = defineProps<{
  dueAmount: Price | null
  nextPayment: {
    date: string
    price: Price
  } | null
  state: PolicyDetails['policy']['state']
  policyId: string
  paymentFrequency: PaymentFrequency
  autoRenew: boolean
  renewalAnniversaryDate: string | null
}>()

const emit = defineEmits(['cancel-auto-renewal'])

// Before the status is changed to ON_HOLD, we try to capture the payment 3 times
const failedPaymentDuringAttemptPeriod = computed(() => {
  return props.state === 'ACTIVE' && props.dueAmount
})

const displayCancelAutoRenewalButton = computed(() => {
  return (
    (props.paymentFrequency === 'ANNUALLY' ||
      props.paymentFrequency === 'BIANNUALLY') &&
    props.autoRenew &&
    (props.state === 'ACTIVE' || props.state === 'ON_HOLD')
  )
})

const updatePaymentMethodButton = computed(() => {
  if (failedPaymentDuringAttemptPeriod.value || props.state === 'ON_HOLD') {
    return {
      label: i18n(translations.catchUpPaymentButton),
      variant: 'primary' as const,
    }
  }

  const recurringPlan =
    props.paymentFrequency === 'ANNUALLY' ||
    props.paymentFrequency === 'BIANNUALLY'

  if (
    props.state === 'ACTIVE' &&
    !props.dueAmount &&
    (props.paymentFrequency === 'MONTHLY' || (props.autoRenew && recurringPlan))
  ) {
    return {
      label: i18n(translations.updatePaymentMethodButton),
      variant: 'secondary' as const,
    }
  }

  return false
})

const onHoldInfoBlockTitle = computed(() => {
  if (props.paymentFrequency === 'MONTHLY') {
    return i18n(translations.onHoldInfoBlockTitleMonthly)
  }

  if (props.paymentFrequency === 'ANNUALLY') {
    return i18n(translations.onHoldInfoBlockTitleAnnually)
  }

  if (props.paymentFrequency === 'BIANNUALLY') {
    return i18n(translations.onHoldInfoBlockTitleBiennually)
  }

  return null
})

const infoBlockContent = computed(() => {
  if (props.paymentFrequency === 'MONTHLY') {
    return i18n(translations.infoBlockContentMonthly)
  }

  if (props.paymentFrequency === 'ANNUALLY') {
    return i18n(translations.infoBlockContentAnnually)
  }

  if (props.paymentFrequency === 'BIANNUALLY') {
    return i18n(translations.infoBlockContentBiennually)
  }

  return null
})
const getPaymentRouteLocation = usePaymentRouteLocation(
  PaymentFlow.INSURANCE_UPDATE_PAYMENT_METHOD,
)

const updatePaymentMethodRoute = computed(() =>
  getPaymentRouteLocation({ policyId: props.policyId }),
)
</script>
